import React, { useEffect, useRef, useState } from "react";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { AppIcon } from "@toothfairy/shared-ui";

const AudioRecorder = ({
  onForcedStop = () => {},
  onRecordingCompleted = () => {},
  onRecordingStarted = () => {},
  autoVoiceMode = false,
  voiceSensitivity = "medium",
  agentColor = null,
  ...props
}) => {
  const { currentTheme } = AppTheme.useTheme();

  const getSensitivityThreshold = (sensitivity) => {
    const thresholds = {
      low: 15,
      medium: 8,
      high: 3,
    };
    return thresholds[sensitivity] || thresholds.medium;
  };
  const [recordedUrl, setRecordedUrl] = useState("");
  const mediaStream = useRef(null);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const audioContext = useRef(null);
  const analyser = useRef(null);
  const silenceTimer = useRef(null);
  const monitoringRef = useRef(false);
  //   track the time elapsed every second
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
      setTimeElapsed(0);
    }
    return () => {
      clearInterval(interval);
      setTimeElapsed(0);
    };
  }, [isRecording]);
  useEffect(() => {
    if (timeElapsed > 600) {
      stopRecording();
      onForcedStop();
    }
  }, [timeElapsed]);
  const startRecording = async () => {
    try {
      setIsRecording(true);
      onRecordingStarted();

      // Check for supported MIME type
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/ogg")
        ? "audio/ogg"
        : null;

      if (!mimeType) {
        throw new Error("No compatible audio format supported in this browser");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStream.current = stream;

      // Set up audio analysis for autoVoiceMode
      if (autoVoiceMode) {
        audioContext.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        analyser.current = audioContext.current.createAnalyser();
        const source = audioContext.current.createMediaStreamSource(stream);
        source.connect(analyser.current);
        analyser.current.fftSize = 256;

        monitoringRef.current = true;

        const monitorAudio = () => {
          if (!monitoringRef.current || !analyser.current) {
            return;
          }

          const bufferLength = analyser.current.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          analyser.current.getByteFrequencyData(dataArray);

          const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;

          const threshold = getSensitivityThreshold(voiceSensitivity);
          if (average < threshold) {
            if (!silenceTimer.current) {
              silenceTimer.current = setTimeout(() => {
                stopRecording();
              }, 1000);
            }
          } else {
            if (silenceTimer.current) {
              clearTimeout(silenceTimer.current);
              silenceTimer.current = null;
            }
          }

          if (monitoringRef.current) {
            requestAnimationFrame(monitorAudio);
          }
        };

        requestAnimationFrame(monitorAudio);
      }

      // Use the detected MIME type
      mediaRecorder.current = new MediaRecorder(stream, { mimeType });

      mediaRecorder.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.current.push(e.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        // Use the same MIME type for the Blob
        const recordedBlob = new Blob(chunks.current, { type: mimeType });
        const url = URL.createObjectURL(recordedBlob);
        setRecordedUrl(url);
        setRecordedBlob(recordedBlob);
        chunks.current = [];
        setIsRecording(false);
        onRecordingCompleted();
      };

      mediaRecorder.current.start();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach((track) => track.stop());
        mediaStream.current = null;
      }
    }
  };

  const stopRecording = () => {
    monitoringRef.current = false;

    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }

    if (audioContext.current) {
      audioContext.current.close();
      audioContext.current = null;
    }

    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.stop();
    }
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => {
        track.stop();
      });
    }
  };
  useEffect(() => {
    return () => {
      monitoringRef.current = false;
      if (silenceTimer.current) {
        clearTimeout(silenceTimer.current);
      }
      if (audioContext.current) {
        audioContext.current.close();
      }
      if (mediaStream.current) {
        mediaStream.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
  }, []);

  useEffect(() => {
    if (recordedUrl != "" && recordedBlob != null) {
      onRecordingCompleted(recordedBlob, recordedUrl);
      setRecordedBlob(null);
      setRecordedUrl("");
    }
  }, [recordedUrl]);

  return (
    <AppIcon
      onPress={isRecording ? stopRecording : startRecording}
      isClickable
      icon={
        isRecording
          ? AppAssets?.icons?.stopGeneration
          : AppAssets?.icons?.microphone
      }
      color={
        isRecording
          ? currentTheme?.theme?.red
          : agentColor
          ? agentColor
          : currentTheme?.theme?.primary
      }
      {...props}
    />
  );
};
export default AudioRecorder;
